import React, { useCallback, useEffect, useState } from 'react';

import { MenuItem, ModalBox, Table } from 'components';
import { useIsMobile } from 'hooks';
import { Code, ToggleOff, Trash } from 'iconsax-react';

import { CreateTokenModal } from './CreateTokenModal';
import { apiClientTokenColumns } from './apiClientTokenColumns';

import { useSelector, useTranslation } from 'third-party';

import {
  DEFAULT_TIMEZONE,
  PersisTableFilters,
  PersisTableSort,
  PersisTables,
} from 'constants/common';

import { useApiClientToken } from 'modules/apiClientToken';

import {
  apiClientTokenSelectors,
  appSelectors,
  settingSelectors,
} from 'selectors';

import { FilterValue } from 'components/Table/types';

import {
  extractFiltersFromSavedColumns,
  mergeSavedColumnsWithExisting,
} from 'utils/app';
import {
  getFromStorage,
  removeFromStorage,
  saveInStorage,
} from 'utils/storage';

import { APIApiClientToken, APISortOrder } from 'types/api';
import { APISettingName } from 'types/backend';

import { palette } from 'styles/theme';

const DEFAULT_TOKEN_SORT = {
  sortField: 'createdOn',
  sortOrder: APISortOrder.DESC,
};

export const ApiClientTokensList = () => {
  const { t } = useTranslation();
  const timezoneValue = useSelector(
    settingSelectors.getSettingValue(APISettingName.Timezone, DEFAULT_TIMEZONE),
  );
  const [columns, setColumns] = useState(apiClientTokenColumns(timezoneValue));
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [loadedUserSettings, setLoadedUserSettings] = useState(false);
  const [isFilteringOn, setIsFilteringOn] = useState(false);

  const {
    loadTokens,
    loadMoreTokens,
    updateTokenFilters,
    clearTokenList,
    clearCreatedToken,
    createToken,
    toggleToken,
    deleteToken,
  } = useApiClientToken();

  const isMobile = useIsMobile();

  const organizationId = useSelector(appSelectors.organizationId);
  const items = useSelector(apiClientTokenSelectors.tokenList);
  const tokensListLoaded = useSelector(
    apiClientTokenSelectors.tokensListLoaded,
  );
  const allTokensLoaded = useSelector(apiClientTokenSelectors.allTokensLoaded);
  const tokenFilters = useSelector(apiClientTokenSelectors.tokenFilters);
  const loadTokensPending = useSelector(
    apiClientTokenSelectors.loadTokensPending,
  );
  const createdToken = useSelector(apiClientTokenSelectors.createdToken);
  const createTokenPending = useSelector(
    apiClientTokenSelectors.createTokenPending,
  );

  useEffect(() => {
    if (organizationId && loadedUserSettings) {
      loadTokens();
    }
  }, [organizationId, tokenFilters, loadedUserSettings, loadTokens]);

  useEffect(() => {
    if (createdToken) {
      setCreateModalOpen(false);
      clearCreatedToken();
    }
  }, [createdToken, clearCreatedToken]);

  useEffect(() => {
    const savedColumns = getFromStorage(
      PersisTables.ApiClientTokens,
      apiClientTokenColumns(timezoneValue),
    );
    const savedSort = getFromStorage(
      PersisTableSort.ApiClientTokens,
      DEFAULT_TOKEN_SORT,
    );

    const columnFilters = savedColumns
      ? extractFiltersFromSavedColumns(savedColumns)
      : {};

    updateTokenFilters({ ...columnFilters, searchSortOrders: [savedSort] });

    if (savedColumns) {
      setColumns(mergeSavedColumnsWithExisting(savedColumns, columns));
    }
    setIsFilteringOn(getFromStorage(PersisTableFilters.ApiClientTokens, false));
    setLoadedUserSettings(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  useEffect(() => {
    return () => {
      clearTokenList();
    };
  }, [clearTokenList]);

  const onAdvancedFilterChange = useCallback(
    (columnId: string, value: FilterValue) => {
      setColumns(
        columns.map(column =>
          column.dataId === columnId
            ? { ...column, filterValue: value }
            : column,
        ),
      );
      updateTokenFilters({ ...tokenFilters, [columnId]: value });
    },
    [tokenFilters, columns, updateTokenFilters],
  );

  const onColumnFilterChange = useCallback(
    (columnId: string, value: string) => {
      setColumns(
        columns.map(column =>
          column.dataId === columnId ? { ...column, filter: value } : column,
        ),
      );
      updateTokenFilters({ ...tokenFilters, [columnId]: value });
    },
    [tokenFilters, columns, updateTokenFilters],
  );

  const onFilterReset = useCallback(() => {
    updateTokenFilters({});
    setColumns(it =>
      it.map(column => ({
        ...column,
        filter: undefined,
        filterValue: undefined,
      })),
    );
  }, [updateTokenFilters]);

  const onColumnVisibilityChanged = useCallback(
    (columnId: string, value: boolean) => {
      setColumns(it =>
        it.map(column =>
          column.dataId === columnId ? { ...column, hidden: !value } : column,
        ),
      );
    },
    [],
  );

  const onColumnHeaderClicked = useCallback(
    (columnDataId: string | number | symbol) => {
      const existingSortOrder = tokenFilters.searchSortOrders?.find(
        order => order.sortField === columnDataId,
      );
      const newSortOrder =
        existingSortOrder && existingSortOrder.sortOrder === APISortOrder.DESC
          ? APISortOrder.ASC
          : APISortOrder.DESC;
      const newSort = {
        sortField: columnDataId as string,
        sortOrder: newSortOrder,
      };
      saveInStorage(PersisTableSort.ApiClientTokens, newSort);
      updateTokenFilters({ ...tokenFilters, searchSortOrders: [newSort] });
    },
    [tokenFilters, updateTokenFilters],
  );

  const rowOptionsMenu = useCallback(
    (
      item: APIApiClientToken,
      onRowMenuClose: () => void,
    ): React.ReactElement[] => [
      <MenuItem
        key="toggle"
        onClick={() => {
          toggleToken(item.id, { isEnabled: !item.isEnabled });
          onRowMenuClose();
        }}
      >
        <ToggleOff
          variant="TwoTone"
          color={palette.primary900}
          style={{ paddingRight: '0.5rem' }}
        />
        {item.isEnabled
          ? t('apiTokens.actions.disable')
          : t('apiTokens.actions.enable')}
      </MenuItem>,
      <MenuItem
        key="delete"
        onClick={() => {
          deleteToken(item.id);
          onRowMenuClose();
        }}
      >
        <Trash
          variant="TwoTone"
          color={palette.primary900}
          style={{ paddingRight: '0.5rem' }}
        />
        {t('table.delete')}
      </MenuItem>,
    ],
    [t, toggleToken, deleteToken],
  );

  return (
    <>
      <Table<APIApiClientToken>
        draggable
        header={t('apiTokens.header')}
        subHeader={t('apiTokens.subHeader')}
        headerIcon={
          <Code variant="TwoTone" color={palette.primary900} size="28" />
        }
        onNewItemButtonClicked={() => setCreateModalOpen(true)}
        onSaveTableSettings={settings => {
          saveInStorage(PersisTables.ApiClientTokens, settings);
        }}
        onResetTableSettings={() => {
          setColumns(apiClientTokenColumns(timezoneValue));
          updateTokenFilters({
            searchSortOrders: [DEFAULT_TOKEN_SORT],
          });
          removeFromStorage(PersisTables.ApiClientTokens);
          removeFromStorage(PersisTableSort.ApiClientTokens);
        }}
        onColumnSizeChanged={(it, width) => {
          setColumns(prevColumns =>
            prevColumns.map(column =>
              column.dataId === it ? { ...column, width: width } : column,
            ),
          );
        }}
        onColumnOrderChanged={setColumns}
        filterable
        filteringOn={isFilteringOn}
        onFilteringToggle={value => {
          setIsFilteringOn(value);
          saveInStorage(PersisTableFilters.ApiClientTokens, value);
        }}
        allItemsLoaded={allTokensLoaded}
        infiniteScroll
        tableMinHeight="20rem"
        columns={columns}
        items={items || []}
        sortField={
          tokenFilters.searchSortOrders?.[0]
            ?.sortField as keyof APIApiClientToken
        }
        sortOrder={tokenFilters.searchSortOrders?.[0]?.sortOrder}
        rowOptionsMenu={rowOptionsMenu}
        onColumnHeaderClicked={onColumnHeaderClicked}
        onColumnFilterChange={onColumnFilterChange}
        onAdvancedFilterChange={onAdvancedFilterChange}
        onColumnVisibilityChanged={onColumnVisibilityChanged}
        loadItemsPending={loadTokensPending}
        onLoadMoreClicked={loadMoreTokens}
        onFilterReset={onFilterReset}
        bodyRowHeight="1rem"
        headerRowHeight="0.2rem"
        itemsLoaded={tokensListLoaded}
        emptyText={t('table.empty')}
        loadMoreButtonText={t('table.loadMore')}
      />

      {createModalOpen && (
        <ModalBox
          width={isMobile ? '90%' : '33rem'}
          height="15rem"
          onClose={() => {
            setCreateModalOpen(false);
            clearCreatedToken();
          }}
        >
          <CreateTokenModal
            createPending={createTokenPending}
            onSubmit={model => createToken(model)}
            onClose={() => {
              setCreateModalOpen(false);
              clearCreatedToken();
            }}
          />
        </ModalBox>
      )}
    </>
  );
};
